import { createApi } from "@reduxjs/toolkit/query/react";
import { apiRequestBaseQuery } from "../../services/rtkBaseQuery";
import { getUserDetails } from "../../services/apiRequest";
import { requestType } from "../../services/requests";

// =============================================================================
// ROYAL BRIDGE - Plans and Targets API
// =============================================================================

// V2: Royal Bridge is on the `*_V2` request types but sends NO agency key (no `agency_id`,
// no `travel_agency_uid`) — HQ-aggregate. See V2_API_MIGRATION.md.

// Helper to build request data for Plans and Targets APIs
const buildRequestData = (params) => {
    const userDetails = getUserDetails();
    return {
        user_uid: userDetails?.user_details?.uid,
        region: params.region === "all" ? null : params.region || null,
        type: params.type || "year",
        start_date: params.startDate,
        end_date: params.endDate || null,
    };
};

export const royalBridgePlansAndTargetsApi = createApi({
    reducerPath: "royalBridgePlansAndTargetsApi",
    baseQuery: apiRequestBaseQuery,
    refetchOnMountOrArgChange: true,
    tagTypes: ["PlansAndTargets"],
    endpoints: (builder) => ({
        // =====================================================================
        // PERFORMANCE VS TARGET
        // =====================================================================

        // Total Revenue
        getTotalRevenue: builder.query({
            query: (params) => ({
                requestType: requestType.getTotalRevenueWithTargetV2,
                data: buildRequestData(params),
            }),
            providesTags: ["PlansAndTargets"],
        }),

        // Total Passengers
        getTotalPassengers: builder.query({
            query: (params) => ({
                requestType: requestType.getTotalPassengersWithTargetV2,
                data: buildRequestData(params),
            }),
            providesTags: ["PlansAndTargets"],
        }),

        // Override Tiers
        getOverrideTiers: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getCommercialTargetV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        year: params.year,
                        quarter: params.quarter,
                    },
                };
            },
            providesTags: ["PlansAndTargets"],
        }),

        // Total Revenue with Target (Performance vs Target card)
        getTotalRevenueWithTarget: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getTotalRevenueWithTargetV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        region: params.region === "all" ? null : params.region || null,
                        year: params.year,
                        quarter: params.quarter,
                    },
                };
            },
            providesTags: ["PlansAndTargets"],
        }),

        // Total Passengers with Target (Performance vs Target card)
        getTotalPassengersWithTarget: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getTotalPassengersWithTargetV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        region: params.region === "all" ? null : params.region || null,
                        year: params.year,
                        quarter: params.quarter,
                    },
                };
            },
            providesTags: ["PlansAndTargets"],
        }),

        // =====================================================================
        // PLANS & GOALS
        // =====================================================================

        // Get Plans
        getPlans: builder.query({
            query: (year) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getPlansV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        year: year,
                    },
                };
            },
            providesTags: ["PlansAndTargets"],
        }),

        // Current Goals — the Progress Tracker.
        //
        // Scoped to one category at a time, so the page calls this once per entry in
        // PLAN_GOAL_CATEGORIES. Returns `current_value`, `target` and a server-computed
        // `target_percentage` per goal, replacing the figures the tracker used to derive from
        // GET_PLANS. Sends no agency key, like every other Bridge endpoint here.
        // params: { year, categoryPlan }
        getCurrentGoals: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getCurrentGoals,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        year: params.year,
                        category_plan: params.categoryPlan,
                    },
                };
            },
            providesTags: ["PlansAndTargets"],
        }),
    }),
});

// Export hooks for usage in components
export const {
    // Performance vs Target
    useGetTotalRevenueQuery,
    useGetTotalPassengersQuery,
    useGetOverrideTiersQuery,
    useGetTotalRevenueWithTargetQuery,
    useGetTotalPassengersWithTargetQuery,
    // Plans & Goals
    useGetPlansQuery,
    useGetCurrentGoalsQuery,
} = royalBridgePlansAndTargetsApi;
